import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface Row {
  id: string;
  type: string;
  status: string | null;
  deleted_at: string;
  deleted_by: string | null;
  delete_reason: string | null;
  appliance: { brand: string | null; model_number: string; serial_number: string | null } | null;
}

/** Superadmin-only (RLS): soft-deleted work orders of one company (FIX-258). */
export function DeletedWorkOrders({ companyId }: { companyId: string }) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [actors, setActors] = useState<Record<string, string>>({});

  useEffect(() => {
    let active = true;
    (async () => {
      const [wo, audit] = await Promise.all([
        supabase
          .from("work_orders")
          .select("id, type, status, deleted_at, deleted_by, delete_reason, appliance:appliances(brand, model_number, serial_number)")
          .eq("company_id", companyId)
          .not("deleted_at", "is", null)
          .order("deleted_at", { ascending: false }),
        supabase
          .from("superadmin_audit_log")
          .select("actor_user_id, actor_email")
          .eq("action", "work_order_deleted")
          .eq("target_company_id", companyId),
      ]);
      if (!active) return;
      setRows((wo.data as unknown as Row[]) ?? []);
      const map: Record<string, string> = {};
      for (const a of audit.data ?? []) if (a.actor_email) map[a.actor_user_id] = a.actor_email;
      setActors(map);
    })();
    return () => { active = false; };
  }, [companyId]);

  return (
    <section className="mt-10 border-t pt-6">
      <h2 className="text-lg font-semibold">Deleted work orders</h2>
      <p className="text-sm text-muted-foreground">Hidden from the company, kept for traceability.</p>
      {rows === null ? (
        <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">No deleted work orders.</p>
      ) : (
        <div className="mt-4 overflow-x-auto border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-muted-foreground">
              <tr>{["ID", "Appliance", "Serial", "Type", "Status at deletion", "Deleted by", "When", "Reason"].map((h) => <th key={h} className="px-3 py-2 font-medium whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y">
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">FIXID-{r.id.slice(0, 8).toUpperCase()}</td>
                  <td className="px-3 py-2">{[r.appliance?.brand, r.appliance?.model_number].filter(Boolean).join(" · ") || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.appliance?.serial_number ?? "—"}</td>
                  <td className="px-3 py-2 capitalize">{r.type}</td>
                  <td className="px-3 py-2">{r.status ?? "in_progress"}</td>
                  <td className="px-3 py-2">{(r.deleted_by && actors[r.deleted_by]) || r.deleted_by || "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{new Date(r.deleted_at).toLocaleString()}</td>
                  <td className="px-3 py-2">{r.delete_reason ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
